<template>
    <form class="col-md-8 mx-auto" @submit.prevent="onSubmit">
        <div class="loader" v-if="loading"></div>
        <div v-if="!loading">
            <div class="btn-group" role="group" aria-label="Basic example">
                <button type="button"
                        class="btn btn-primary"
                        @click="backToRegister">
                    <i class="fas fa-th-list"></i> {{ $t('backToList') }}
                </button>
            </div>
            <page-title :title="$t('places.title')"></page-title>

            <div class="card row">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('places.location') }}</div>
                </div>
                <div class="card-body">
                    <div class="form-group col-12">
                        <label :for="'placeName' + _uid" class="required">{{ $t('places.name') }}</label>
                        <input type="text" class="form-control"
                               :id="'placeName' + _uid"
                               :name="'placeName' + _uid"
                               v-model="place.name"
                               v-validate="isDifferentPlaceName? 'required|unique:/api/Places/IsPlaceNameUnique':'required'"
                               :data-vv-as="$t('places.name')"/>
                        <span class="text-danger" v-if="errors.has('placeName' + _uid)">{{ errors.first('placeName' + _uid) }}</span>
                    </div>
                </div>
            </div>
            <div class="card row mt-5">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('places.halls') }}</div>
                </div>
                <div class="card-body">
                    <div v-for="(hall,index) in place.halls" :key="index" class="k-display-flex">
                        <hall :hall="hall" class="col-12"></hall>
                        <i class="fas fa-trash-alt btn-danger position-relative align-self-center pointer"
                           @click="deleteHall(hall,index)"></i>
                    </div>
                    <button class="btn btn-primary" @click="addHall">{{ $t('places.addHall') }}</button>
                </div>
            </div>
            <div class="card row mt-5">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('address.cardTitle') }}</div>
                </div>
                <div class="card-body">
                    <app-address :required="false" :address="place.address"></app-address>
                </div>
            </div>
            <div class="card row mt-5">
                <div class="card">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('contactPerson') }}</div>
                    </div>
                    <div v-if="showContactPerson">
                        <div class="card-body">
                            <contact-person :person="place.contactPerson"></contact-person>
                        </div>
                    </div>
                    <div class="text-center m-1">
                        <div v-if="!showContactPerson">
                            <button type="button" class="btn btn-primary btn-md mr-2"
                                    @click="setContactDetails">
                                <i class="fas fa-user-plus"></i>
                                {{ $t('add') }}
                            </button>
                        </div>
                        <div v-else>
                            <button type="button"
                                    class="btn btn-primary"
                                    @click="setContactDetails">
                                <i class="fas fa-user-minus"></i> {{ $t('remove') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card row mt-5">
                <div class="card">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('places.otherFields') }}</div>
                    </div>
                    <div class="card-body">
                        <otherfields :otherFields="place.otherFields"></otherfields>
                    </div>
                </div>
            </div>

            <AuxiliaryRegister ref="auxiliaryRegisterRef"
                               :register-id="AuxiliaryRegisters.Venues"
                               register-name="Places"
                               :entry-id="place.auxRegisterEntityId"></AuxiliaryRegister>

            <div class="text-center mt-2 mb-5">
                <button type="button" class="btn btn-primary btn" @click="onSubmit">
                    <i class="fas fa-save"></i>
                    {{ $t('buttons.save.btnText') }}
                </button>
            </div>
        </div>
    </form>
</template>

<script>
    import HallComponent from '../../../components/hall/Hall';
    import AddressComponent from '../../../components/address/Address';
    import { Person, Place, Hall } from '@/models/models';
    import ContactPersonComponent from '../../../components/person/ContactPerson';
    import OtherFields from '../../../components/otherFields/OtherFields.vue';
    import PageTitle from '../../../components/common/PageTitle';
    import { AuxiliaryRegisters, Culture } from '@/models/enums';
    import AuxiliaryRegister from '@/components/dynamic/AuxiliaryRegister';

    export default {
        components: {
            AuxiliaryRegister,
            'contact-person': ContactPersonComponent,
            'hall': HallComponent,
            'app-address': AddressComponent,
            'otherfields': OtherFields,
            PageTitle
        },
        data() {
            return {
                place: new Place(),
                id: null,
                loading: false,
                isDifferentPlaceName: false,
                showContactPerson: false,
                placeNameOld: '',
                isSaved: false,
                AuxiliaryRegisters: AuxiliaryRegisters
            }
        },
        methods: {
            addHall(event) {
                this.place.halls.push({ name: '', capacity: 0 });
            },
            deleteHall(hall, index) {
                if (hall.id !== undefined) {
                    this.place.deletedHallsId.push(hall.id);
                }
                this.place.halls.splice(index, 1);
            },
            setContactDetails() {
                this.showContactPerson = !this.showContactPerson;
                if (this.showContactPerson) {
                    this.place.contactPerson = new Person();
                } else {
                    this.place.contactPerson = null;
                }
            },
            onSubmit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.place.auxRegisterModel = this.$refs.auxiliaryRegisterRef.getDynamicRegisterModelForSubmit();
                        if (this.id) {
                            this.edit();
                        } else {
                            this.create();
                        }
                    }
                })
            },
            backToRegister() {
                this.$router.push('/registers/places');
            },
            create() {
                this.loading = true;
                this.$http.post('/api/places', this.place).then(result => {
                    this.loading = false;
                    this.isSaved = true;
                    this.$vs.notify({
                        title: this.$t('notifications.success'),
                        text: this.$t('notifications.successfulSavedData'),
                        position: 'top-center',
                        color: 'success'
                    });
                    this.backToRegister();
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.error'),
                        text: this.$helpers.errorText(error),
                        position: 'top-center',
                        color: 'danger'
                    });
                });
            },
            edit() {
                this.loading = true;
                this.$http.put('/api/places', this.place).then(result => {
                    this.loading = false;
                    this.isSaved = true;
                    this.$vs.notify({
                        title: this.$t('notifications.success'), // TODO:
                        text: this.$t('notifications.successfulSavedData'), // TODO:
                        position: 'top-center',
                        color: 'success'
                    });
                    this.backToRegister();
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.error'), // TODO:
                        text: this.$helpers.errorText(error), // TODO:
                        position: 'top-center',
                        color: 'danger'
                    });
                });
            },
            loadData() {
                this.loading = true;
                this.$http.get(`/api/places/${this.id}`).then(result => {
                    this.place = new Place(result.data);
                    this.$store.initialModel = _.cloneDeep(this.place);
                    this.isDifferentPlaceName = false;
                    this.oldPlaceName = this.place.name;
                    this.loading = false;
                    if (this.place.contactPerson.firstName !== "") {
                        this.showContactPerson = true;
                    }
                }).catch(error => {
                    // TODO: Notify
                    this.loading = false;
                });
            },
        },
        computed: {
            isBgCulture() {
                return this.$store.getters.currentCulture === Culture.BG;
            }
        },
        mounted() {
            this.id = this.$route.params.id;
            if (this.id) {
                this.loadData();
            } else {
                this.place.halls.push(new Hall());
                this.$store.initialModel = _.cloneDeep(this.place);
            }
        },
        beforeRouteLeave(to, from, next) {
            console.log(this.$store.initialModel);
            if (!_.isEqual(this.$store.initialModel, this.place) && !this.isSaved) {
                const answer = window.confirm(this.$t('notifications.leavePage'))
                if (answer) {
                    next()
                } else {
                    next(false)
                }
            } else {
                next()
            }
        },
        watch: {
            'place.name'(newVal, oldVal) {
                if (newVal === this.oldPlaceName) {
                    this.isDifferentPlaceName = false;
                } else {
                    this.isDifferentPlaceName = true;
                }
            },
        },
    }
</script>

<style scoped>
</style>
